RivalIQ.module('data', function (data, App, Backbone, Marionette, $, _, moment) {

    'use strict';

    const STALE_CACHE_REPORT_MS = 2000;
    let numStaleCacheLoads = 0;
    const _debouncedReportStaleCacheLayer = _.debounce((cacheName, currentDataVersion, cacheDataVersion) => {
        RivalIQ.logger.info(`${cacheName} potentially stale cache ${currentDataVersion} vs ${cacheDataVersion}; total ${numStaleCacheLoads}`);
        const cacheLoadsToReport = numStaleCacheLoads;
        numStaleCacheLoads = 0;

        $.post('/api/staleCacheReport', {
            landscapeId: RivalIQ.state.landscape.get('id'),
            currentDataVersion: currentDataVersion,
            cacheDataVersion: cacheDataVersion,
            cacheLoadsToReport: cacheLoadsToReport
        });
    }, STALE_CACHE_REPORT_MS);

    function reportStaleCacheLayer(cacheName, currentDataVersion, matchDataVersion) {
        numStaleCacheLoads++;
        _debouncedReportStaleCacheLayer(cacheName, currentDataVersion, matchDataVersion);
    }

    function getCurrentDataVersion() {
        return RivalIQ.state.landscape._latest_data_version || RivalIQ.state.landscape.get('data_version');
    }

    // Cache by time period start/end dates so that:
    //   * Change in custom date range busts the cache
    //   * We share caches between pre-defined date ranges and custom date ranges
    //   * Temporality of relative dates (last x days) is respected
    const getTimePeriodKey = {
        value: function (attributes) {
            const timePeriodStrings = _.map(['mainPeriodStart',
                'mainPeriodEnd',
                'comparePeriodStart',
                'comparePeriodEnd'], function (k) {
                return moment.utc(attributes.timePeriod[k]).startOf('day').format('x');
            });

            return timePeriodStrings.join('-');
        },
        test: _.isEqual
    };

    let buffering = false;

    data.CacheLayer = Marionette.Object.extend({

        cacheEntryTTLSeconds: -1, // forever

        process: function (cache) {
            return cache.deferred;
        },

        isCached: function (attributes) {
            const me = this;
            const cache = me.get(attributes);

            return Boolean(cache) && cache.deferred.state() === 'resolved';
        },

        _resolveFromMatchingRequest: function (cache, data) {
            cache.dataVersion = getCurrentDataVersion();

            cache.data.deferred.resolve(data);
        },

        _loadData: function (attributes, cache) {
            const me = this;

            // capture the invalidationId before we issue the request; if this changes while the
            // request is inflight then we should ignore the results.
            const requestInvalidationId = me._invalidationId;

            RivalIQ.logger.info(`Fetching ${me.name} from server`);

            const cacheValues = _.find(me.caches, {
                data: cache
            }).cacheValues;
            // Mark all matching request not lazy, since we're about to go get them
            _.each(me.getMatching(attributes, cacheValues), function (match) {
                match.data.lazy = false;
            });

            me.loadData(attributes, cache)
                .done(function (obj) {
                    if (requestInvalidationId === me._invalidationId) {
                        cache._loadedAt = new Date();
                        // Resolve all matching requests.
                        _.each(me.getMatching(attributes, cacheValues), function (match) {
                            me._resolveFromMatchingRequest(match, obj);
                        });

                        RivalIQ.execute('cacheLayer:load:done', obj, attributes, cache);
                    }
                })
                .fail(function (error) {
                    // Reject all matching requests.
                    _.each(me.getMatching(attributes, cacheValues), function (match) {
                        match.data.deferred.reject(error);
                    });

                    RivalIQ.execute('cacheLayer:load:fail', error, attributes, cache);
                });
        },

        /**
         * Gets or loads a data instance from attributes.
         * @param attributes
         */
        getOrLoad: function (attributes, options) {
            const me = this;

            options = _.extend({}, options);
            _.defaults(attributes, {
                lazy: buffering
            });

            let cache = me.get(attributes);

            // If matching is lazy but this fetch is not, go get data
            if (cache && options.force !== true) {
                if (cache.lazy && !attributes.lazy) {
                    cache.lazy = false;
                    me._loadData(attributes, cache);
                }

                return me.process(cache, attributes);
            }

            if (!cache || options.force) {
                cache = me._addCollection(attributes);
            }

            if (!cache.lazy) {
                me._loadData(attributes, cache);
            }

            return me.process(cache, attributes);
        },

        matches: function (cacheA, cacheB) {
            const me = this;

            for (let i = 0; i < me.compiledCacheKeys.length; i++) {
                const same = me.compiledCacheKeys[i].test(cacheA.cacheValues[i], cacheB.cacheValues[i], cacheA.data.attributes);

                if (!same) {
                    return false;
                }
            }

            return true;
        },

        getInflight: function () {
            const me = this;

            return _.filter(me.caches, function (cache) {
                return (cache.data.deferred.state() === 'pending');
            });
        },

        getMatching: function (attrs, resolvedCacheValues) {
            const me = this;

            return _.filter(me.caches, function (cache) {
                let isCached = true;
                let keyIdx = 0;

                if (cache.data.deferred.state() === 'rejected') {
                    isCached = false;
                }

                while (isCached && me.compiledCacheKeys[keyIdx]) {
                    const ck = me.compiledCacheKeys[keyIdx];

                    if (resolvedCacheValues) {
                        isCached = ck.test(resolvedCacheValues[keyIdx], cache.cacheValues[keyIdx], cache.data.attributes);
                        if (data.CacheLayer.enableDebugLogging) {
                            RivalIQ.logger.debug(
                                `${keyIdx} ${ck} ${JSON.stringify(resolvedCacheValues[keyIdx])} ${JSON.stringify(cache.cacheValues[keyIdx])} ${JSON.stringify(cache.data.attributes)} ${isCached}`
                            );
                        }
                    } else {
                        isCached = ck.test(cache.cacheValues[keyIdx], ck.value(attrs), attrs);
                        if (data.CacheLayer.enableDebugLogging) {
                            RivalIQ.logger.debug(
                                `${keyIdx} ${ck} ${JSON.stringify(cache.cacheValues[keyIdx])} ${JSON.stringify(ck.value(attrs))} ${JSON.stringify(attrs)} ${isCached}`
                            );
                        }
                    }

                    keyIdx++;
                }

                return isCached;
            });

        },

        get: function (attrs, insert, force) {
            const me = this;
            attrs = attrs || {};

            const matching = (force ? [] : me.getMatching(attrs));

            if (matching.length) {
                // Prefer finished requests
                const match = _.find(matching, function (match) {
                    return match.data.deferred.state() === 'resolved';
                }) || matching[0];

                if (match.data.deferred.state() === 'resolved') {
                    const currentDataVersion = getCurrentDataVersion();
                    const matchDataVersion = match.dataVersion;

                    if (matchDataVersion && currentDataVersion !== matchDataVersion) {
                        reportStaleCacheLayer(me.name, currentDataVersion, matchDataVersion);
                    }
                }
                return match.data;
            }

            if (!insert) {
                return null;
            }

            const newCacheValues = _.map(me.compiledCacheKeys, function (ck) {
                return ck.value(attrs);
            });

            me.caches.push({
                cacheValues: newCacheValues,
                data: insert,
                dataVersion: getCurrentDataVersion()
            });

            return insert;
        },

        _buildCacheValue: function (attrs) {
            return {
                attributes: attrs,
                lazy: attrs && attrs.lazy,
                deferred: new $.Deferred()
            };
        },

        _addCollection: function (attrs) {
            const me = this;

            const val = me._buildCacheValue(attrs);

            me.triggerMethod('add', attrs, val);

            const result = me.get(attrs, val, true);

            const newCache = _.last(me.caches);

            /*
             * Mark existing matching requests that will be covered by this new
             * addition 'do not fetch'. This allows us to further consolidate
             * API requests
             */
            _.each(me.caches, function (cache) {
                if (cache !== newCache && cache.data.lazy && me.matches(cache, newCache)) {
                    cache.data.doNotFetch = true;
                }
            });


            return result;
        },

        // Adds a model to the collection specified by the cache keys
        addModel: function (model, cacheKeyValues, options) {
            const me = this;
            options = options || {};

            const retrievalFn = (options.fetchIfNotPresent ? _.partial(me.getOrLoad, _, options) : me.get);
            const deferred = _.get(retrievalFn.call(me, cacheKeyValues), 'deferred');

            if (deferred) {
                deferred.done(function (collection) {
                    collection.add(model);
                });
            }
        },

        /*
         * Resolve all lazy caches
         */
        flush: function () {
            const me = this;

            _.each(me.caches, function (cache) {
                if (cache.data.lazy && !cache.data.doNotFetch) {
                    me._loadData(cache.data.attributes, cache.data);
                }
            });
        },

        getCacheFn: function (key) {
            const me = this;
            return _.find(me.compiledCacheKeys, {
                key: key
            }).test;
        },

        initialize: function () {
            const me = this;

            me.caches = [];

            me.compiledCacheKeys = _.map(me.cacheKeys, function (cacheKey) {
                if (_.isString(cacheKey)) {
                    return {
                        key: cacheKey,
                        value: _.partial(_.get, _, cacheKey),
                        test: _.isEqual
                    };
                }

                if (_.isString(cacheKey.value)) {
                    cacheKey.key = cacheKey.value;
                    cacheKey.value = _.partial(_.get, _, cacheKey.key);
                }

                return cacheKey;
            });

            me.resetInvalidationId();

            data.CacheLayer.register(me);
        },

        // The invalidationId is changed whenever the cache is invalidated; that way, we can see if
        // the cache was invalidated while this request was inflight and, if so, ignore the results.
        resetInvalidationId: function () {
            const me = this;

            me._invalidationId = _.uniqueId(me.name);
        },

        removeStaleCacheEntries: function () {
            const me = this;

            if (me.caches && me.cacheEntryTTLSeconds > 0) {
                const now = new Date();
                _.remove(me.caches, function (cache) {
                    return cache._loadedAt &&
                        (now - cache._loadedAt) > me.cacheEntryTTLSeconds * 1000;
                });
            }
        },

        // TODO cleanup probably needed
        invalidateCaches: function (reissueInflightRequests) {
            const me = this;

            me.resetInvalidationId();

            if (reissueInflightRequests) {
                // re-request the inflight requests
                me.caches = me.getInflight();
                _.each(me.caches, function (cache) {
                    _.set(cache, ['data', 'lazy'], true);
                });
                me.flush();
            } else {
                me.caches = [];
            }
        },

        set: function (attrs, value) {
            const me = this;
            const cache = me._addCollection(attrs);

            cache.dataVersion = getCurrentDataVersion();

            cache.deferred.resolve(value);
            me.process(cache, attrs);
            return cache;
        },

        create: function (attrs) {
            const me = this;
            const cache = me._addCollection(attrs);
            me.process(cache, attrs);
            return cache;
        },

        fetch: function (attrs, options) {
            const me = this;
            return me.getOrLoad(attrs, options);
        }
    });

    data.CacheLayer.keyType = {
        TIMEPERIOD: getTimePeriodKey
    };

    let cacheLayers = [];

    data.CacheLayer.startBuffering = function () {
        buffering = true;

        _.invokeMap(cacheLayers, 'removeStaleCacheEntries');
    };

    data.CacheLayer.stopBuffering = function () {
        buffering = false;
    };

    data.CacheLayer.isBuffering = function () {
        return buffering;
    };

    data.CacheLayer.register = function (cacheLayer) {
        cacheLayers = _.union(cacheLayers, [cacheLayer]);
    };

    data.CacheLayer.flush = function () {
        RivalIQ.logger.info('Flushing data requests');

        _.invokeMap(cacheLayers, 'flush');

        RivalIQ.logger.info('Done flushing data requests');
    };

    data.CacheLayer.invalidateAllCaches = function (reissueInflightRequests) {
        // If we're not already buffering, we want to be sure to start buffering here, since we'll potentially be making
        // several metric loads. At the end of this method, if we were the one who started buffering we'll make sure to
        // stop buffering.
        const shouldStartBuffering = !data.CacheLayer.isBuffering();
        if (shouldStartBuffering) {
            data.CacheLayer.startBuffering();
        }

        const shouldStartUniversalBuffering = !RivalIQ.universal._isBuffering;
        if (shouldStartUniversalBuffering) {
            RivalIQ.universal.startBuffering();
        }

        _.invokeMap(cacheLayers, 'invalidateCaches', reissueInflightRequests);

        if (shouldStartBuffering) {
            data.CacheLayer.stopBuffering();
        }

        if (shouldStartUniversalBuffering) {
            RivalIQ.universal.stopBuffering();
        }
    };

    data.CacheLayer.enableDebugLogging = false;


    data.BatchCacheLayer = data.CacheLayer.extend({

        cacheEntryTTLSeconds: 15 * 60, // 15 minutes.

        _loadDataForCaches: function (groupCachesToLoad, requestGroup) {
            const me = this;

            // capture the invalidationId before we issue the request; if this changes while the
            // request is inflight then we should ignore the results.
            const requestInvalidationId = me._invalidationId;

            RivalIQ.logger.info(`Fetching ${groupCachesToLoad.length} requests for ${me.name} (${requestGroup}) from server`);

            // Mark all matching request not lazy, since we're about to go get them
            _.each(groupCachesToLoad, function (cache) {
                cache.data.lazy = false;
            });

            return me.loadData(_.map(groupCachesToLoad, 'data.attributes'))
                .done(function (results) {
                    if (requestInvalidationId === me._invalidationId) {
                        const loadedAt = new Date();
                        // Resolve all matching requests.
                        _.each(groupCachesToLoad, function (cache, idx) {
                            cache._loadedAt = loadedAt;

                            const obj = results[idx];
                            _.each(me.getMatching(cache.attributes, cache.cacheValues), function (match) {
                                me._resolveFromMatchingRequest(match, obj);
                            });

                            RivalIQ.execute('cacheLayer:load:done', obj, cache.attributes, cache);
                        });
                    }
                })
                .fail(function (error) {
                    _.each(groupCachesToLoad, function (cache) {
                        cache.data.deferred.reject(error);
                        RivalIQ.execute('cacheLayer:load:fail', error, cache.attributes, cache);
                    });
                });
        },

        // any request to _loadData should result in a call to the server with all metrics requested.
        _loadData: function (attributes, cacheToLoad) {
            const me = this;

            const cachesToLoad = _.filter(me.caches, function (cache) {
                return cache.data === cacheToLoad || (cache.data.lazy && !cache.data.doNotFetch);
            });

            const groupedCachesToLoad = _.groupBy(cachesToLoad, function (cache) {
                return _.get(cache, ['data', 'attributes', 'requestGroup']) || 'DefaultRequestGroup';
            });

            _.each(groupedCachesToLoad, function (groupCachesToLoad, requestGroup) {
                me._loadDataForCaches(groupCachesToLoad, requestGroup);
            });
        }
    });

}, window.moment);
